import { api } from './api.service';
import { ISchedules } from './journey.service';
import { Pagination } from '../entities/Pagination';

interface IExameConfirmation {
  calendarid: string;
  journeyid: string;
  dateStart: string;
  timezone?: string;
}

export interface IReschedulingConfirmation {
  dateStart: string;
  currentScheduleId: string;
  calendarid?: string;
  userId?: string;
  reason?: string | null;
}

interface IScheduleReturnConfirmation {
  calendarid: string;
  journeyid: string;
  dateStart: string;
  returnSchedule: boolean;
  userid: string;
  timezone?: string;
}

export interface VerifyActiveScheduleDTO {
  id: string;
  dateStart: string;
  allowReschedule: boolean;
  token: string;
  journey: {
    id: string;
  };
  user: {
    name: string;
  };
}

export type IAttendanceDeclarationResponse = {
  code: string;
  createdAt: string;
  patient: {
    fullName: string;
    cpf: string;
  };
  schedule: {
    dateStart: string;
    dateEnd: string;
  };
  calendar: {
    location: string | null;
  };
};

export type ConfirmScheduleResponseDTO = {
  journey: {
    user: {
      name: string;
      profileImage: string | null;
    };
  };
};

export const schedule = api.injectEndpoints({
  endpoints: build => ({
    scheduleExameConfirmation: build.mutation<any, IExameConfirmation>({
      query: body => ({
        url: `/schedule`,
        method: 'POST',
        body
      })
    }),
    reschedulingConfirmation: build.mutation<any, IReschedulingConfirmation>({
      query(data) {
        const { currentScheduleId, ...body } = data;
        return {
          url: `/schedule/${currentScheduleId}/reschedule`,
          method: 'PUT',
          body
        };
      }
    }),
    scheduleReturnConfirmation: build.mutation<any, IScheduleReturnConfirmation>({
      query: body => ({
        url: `/schedule`,
        method: 'POST',
        body
      })
    }),

    cancelScheduleById: build.mutation<any, { id: string }>({
      query: ({ id }) => ({
        url: `/portal/v1/schedule/cancel/${id}`,
        method: 'PUT'
      })
    }),

    cancelScheduleByIdWithToken: build.mutation<any, { id: string; token: string }>({
      query: ({ id, token }) => ({
        url: `/portal/v1/schedule/cancel/${id}`,
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`
        }
      })
    }),

    cancelScheduleReasonById: build.mutation<any, { id: string; reason: string; token: string }>({
      query(data) {
        const { id, reason, token } = data;
        return {
          url: `/portal/v1/schedule/cancelReason/${id}`,
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: {
            reason: reason
          }
        };
      }
    }),
    verifyActiveScheduleById: build.mutation<VerifyActiveScheduleDTO | undefined, { id: string }>({
      query: ({ id }) => ({
        url: `/portal/v1/verifyActiveSchedule/${id}`,
        method: 'GET'
      })
    }),

    confirScheduleById: build.mutation<ConfirmScheduleResponseDTO, { id: string; token: string }>({
      query(data) {
        const { id, token } = data;
        return {
          url: `/portal/v1/confirmSchedule/${id}`,
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`
          }
        };
      }
    }),

    createAttendanceDeclaration: build.mutation<IAttendanceDeclarationResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/schedule/${id}/attendance-declaration`,
        method: 'POST'
      })
    }),

    validateAttendanceDeclaration: build.mutation<IAttendanceDeclarationResponse, { code: string }>({
      query: ({ code }) => ({
        url: `/schedule/attendance-declaration/${code}`,
        method: 'GET',
        headers: {
          Authorization: `Bearer oya-care`
        }
      })
    }),

    getFindAllAttendanceDeclaration: build.mutation<
      { items: ISchedules[]; total: number; page: number; quantity: number },
      Pagination
    >({
      query: params => ({
        url: `/portal/v1/me/schedule/attendance-declaration`,
        method: 'GET',
        params
      })
    })
  })
});

export const {
  useScheduleExameConfirmationMutation,
  useReschedulingConfirmationMutation,
  useScheduleReturnConfirmationMutation,
  useCancelScheduleByIdMutation,
  useCancelScheduleByIdWithTokenMutation,
  useCancelScheduleReasonByIdMutation,
  useVerifyActiveScheduleByIdMutation,
  useConfirScheduleByIdMutation,
  useCreateAttendanceDeclarationMutation,
  useValidateAttendanceDeclarationMutation,
  useGetFindAllAttendanceDeclarationMutation
} = schedule;
